import { useEffect, useMemo, useRef } from "react";

import { FormControl } from "@client:common/form";
import { Theme } from "@client:common/Theme";

import "./ThemeToggle.dark.scss";
import "./ThemeToggle.light.scss";

interface Props {
  onThemeChange: (theme: Theme) => void;
}

export function ThemeToggle(props: Props) {
  const previouslySavedTheme = useMemo(() => {
    return (localStorage.getItem("theme") as Theme) || Theme.DARK;
  }, []);
  const isDarkThemeSelectedFormControl = useMemo(
    () => new FormControl(previouslySavedTheme === Theme.DARK),
    [previouslySavedTheme]
  );
  const toggleTheme = useRef((isDarkThemeSelected: boolean) => {
    // These variables are injected by webpack
    // They are declared in src/webpack-injections-tokens.d.ts
    const styleFilename = isDarkThemeSelected
      ? __DARK_THEME_STYLE_FILENAME__
      : __LIGHT_THEME_STYLE_FILENAME__;
    const link = document.head.querySelector<HTMLLinkElement>(
      "link[rel=stylesheet]:last-of-type"
    );
    const newTheme = isDarkThemeSelected ? Theme.DARK : Theme.LIGHT;

    link.href = "/" + styleFilename;
    localStorage.setItem("theme", newTheme);
    props.onThemeChange(newTheme);
  });

  useEffect(() => {
    const subscription = isDarkThemeSelectedFormControl
      .valueChanges()
      .subscribe({
        next: toggleTheme.current
      });
    if (previouslySavedTheme === "light") {
      toggleTheme.current(false);
    }
    return () => {
      subscription.unsubscribe();
    };
  }, [isDarkThemeSelectedFormControl, previouslySavedTheme]);

  return (
    <span
      className="theme-toggle material-icons"
      onClick={() =>
        isDarkThemeSelectedFormControl.setValue(
          !isDarkThemeSelectedFormControl.getValue()
        )
      }
    >
      {isDarkThemeSelectedFormControl.getValue() ? "light_mode" : "dark_mode"}
    </span>
  );
}
